/**
 * Utility functions for opening document detail modals globally
 */

const PASCALE_MODAL_RETURN_KEY = 'pascale_document_modal_return';

/**
 * Opens a document detail modal by updating URL parameters
 * @param {string} documentId - The ID of the document to display
 * @param {string} history - React Router history object
 * @param {string} location - React Router location object
 * @param {string} [companyId] - Empresa dueña del documento, si no es la seleccionada
 *   (p. ej. las facturas de Smart CFO que muestra la Ficha Cliente)
 */
export const openDocumentDetail = (documentId, history, location, companyId) => {
  const currentParams = new URLSearchParams(location.search);
  currentParams.set("id", documentId);
  currentParams.set("isDocumentDetail", "true");
  if (companyId) currentParams.set("docCompanyId", companyId);
  else currentParams.delete("docCompanyId");
  history.push({
    pathname: location.pathname,
    search: currentParams.toString(),
    hash: location.hash || "",
  });
};

/**
 * Abre el detalle desde Pascale: guarda la URL actual y navega al módulo correspondiente.
 * @param {string} documentId - Mongo _id del documento
 * @param {object} history
 * @param {object} location
 * @param {{ modulePath?: string, extraSearch?: Record<string, string> }} options
 */
export const openPascaleDocumentDetail = (
  documentId,
  history,
  location,
  { modulePath = '/pago_proveedores', extraSearch = {} } = {},
) => {
  if (!documentId) return;
  try {
    sessionStorage.setItem(
      PASCALE_MODAL_RETURN_KEY,
      JSON.stringify({
        pathname: location.pathname,
        search: location.search || '',
      }),
    );
  } catch (_) {
    /* ignore */
  }
  const params = new URLSearchParams();
  Object.entries(extraSearch || {}).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });
  params.set('id', documentId);
  params.set('isDocumentDetail', 'true');
  history.push({
    pathname: modulePath,
    search: params.toString(),
  });
};

export const isPascaleDocumentModalSession = () => {
  try {
    return Boolean(sessionStorage.getItem(PASCALE_MODAL_RETURN_KEY));
  } catch (_) {
    return false;
  }
};

/**
 * Restaura la URL previa a abrir el modal desde Pascale (si aplica).
 * @returns {boolean} true si se restauró la sesión Pascale
 */
export const closePascaleDocumentDetailIfNeeded = (history) => {
  try {
    const raw = sessionStorage.getItem(PASCALE_MODAL_RETURN_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(PASCALE_MODAL_RETURN_KEY);
    const { pathname, search } = JSON.parse(raw);
    history.replace({
      pathname: pathname || '/',
      search: search || '',
    });
    return true;
  } catch (_) {
    sessionStorage.removeItem(PASCALE_MODAL_RETURN_KEY);
    return false;
  }
};

/**
 * Quita parámetros del modal de detalle sin restaurar (p. ej. antes de exportar en Pascale).
 */
export const stripDocumentDetailFromUrl = (history, location) => {
  const params = new URLSearchParams(location.search);
  const hadModal =
    params.get('isDocumentDetail') === 'true' || Boolean(params.get('id'));
  if (!hadModal) return false;
  params.delete('id');
  params.delete('isDocumentDetail');
  params.delete('isGlobalSearch');
  params.delete('docCompanyId');
  history.replace({
    pathname: location.pathname,
    search: params.toString(),
  });
  try {
    sessionStorage.removeItem(PASCALE_MODAL_RETURN_KEY);
  } catch (_) {
    /* ignore */
  }
  return true;
};

/**
 * Cierra el document detail modal limpiando parámetros de URL
 * @param {string} history - React Router history object
 * @param {string} location - React Router location object
 */
export const closeDocumentDetail = (history, location) => {
  if (closePascaleDocumentDetailIfNeeded(history)) return;
  const currentParams = new URLSearchParams(location.search);
  currentParams.delete("id");
  currentParams.delete("isDocumentDetail");
  currentParams.delete("isGlobalSearch");
  currentParams.delete("docCompanyId");
  history.replace({
    pathname: location.pathname,
    search: currentParams.toString(),
    hash: location.hash || "",
  });
};

/**
 * Hook to get document detail modal state from URL
 * @param {string} location - React Router location object
 * @returns {Object} - { isVisible, documentId }
 */
export const useDocumentDetailState = (location) => {
  const searchParams = new URLSearchParams(location.search);
  const id = searchParams.get('id');
  const isDocumentDetail = searchParams.get('isDocumentDetail');
  
  return {
    isVisible: !!(id && isDocumentDetail),
    documentId: id,
    companyId: searchParams.get('docCompanyId') || undefined,
  };
};

/**
 * React Hook for using document detail modal in components
 * @param {string} history - React Router history object
 * @param {string} location - React Router location object
 * @returns {Object} - { openDocumentDetail, closeDocumentDetail, isVisible, documentId }
 */
export const useDocumentModal = (history, location) => {
  const { isVisible, documentId } = useDocumentDetailState(location);
  
  const openModal = (docId) => {
    openDocumentDetail(docId, history, location);
  };
  
  const closeModal = () => {
    closeDocumentDetail(history, location);
  };
  
  return {
    openDocumentDetail: openModal,
    closeDocumentDetail: closeModal,
    isVisible,
    documentId
  };
};
